import { useEffect, useState } from "react";
import api, { rupiah, errMsg, exportReport } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Wallet, CheckCircle2, Circle, BellRing, FileDown } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { supplier: "", description: "", amount: 0, date: today(), due_date: "" };

export default function Debts() {
  const { user } = useAuth();
  const [debts, setDebts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/debts").then((r) => setDebts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const doExport = async (fmt) => {
    try { await exportReport("debts", fmt, { filename: "laporan_hutang" }); toast.success("Laporan diunduh"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/debts", { ...form, amount: Number(form.amount) });
      toast.success("Hutang dicatat");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const togglePay = async (id) => {
    try { await api.patch(`/debts/${id}/pay`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus catatan hutang ini?")) return;
    try { await api.delete(`/debts/${id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const shown = debts.filter((d) => filter === "all" || (filter === "unpaid" ? !d.is_paid : d.is_paid));
  const outstanding = debts.filter((d) => !d.is_paid).reduce((s, d) => s + d.amount, 0);
  const dueInfo = (d) => {
    if (d.is_paid || !d.due_date) return null;
    const days = Math.ceil((new Date(d.due_date) - new Date(today())) / 86400000);
    if (days < 0) return { label: `Telat ${-days}h`, color: "var(--retur)" };
    if (days === 0) return { label: "Tempo hari ini", color: "var(--retur)" };
    if (days <= 3) return { label: `H-${days}`, color: "var(--keluar)" };
    return null;
  };
  const reminders = debts.filter((d) => dueInfo(d));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Hutang Supplier</h1>
          <p className="text-[#52525B] text-sm">Catat & tandai lunas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="export-debts-excel" onClick={() => doExport("excel")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> Excel</button>
          <button data-testid="export-debts-pdf" onClick={() => doExport("pdf")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> PDF</button>
          <button data-testid="add-debt-button" onClick={() => setOpen(true)} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
            <Plus className="w-5 h-5" strokeWidth={2.5} /> Tambah Hutang
          </button>
        </div>
      </div>

      <div className="nb-card p-5" style={{ background: "var(--retur)" }} data-testid="debt-outstanding">
        <span className="label-tag">Total Hutang Belum Lunas</span>
        <div className="font-display font-extrabold text-3xl mt-1">{rupiah(outstanding)}</div>
      </div>

      {reminders.length > 0 && (
        <div className="nb-card p-4 flex items-center gap-3" data-testid="debt-reminder" style={{ background: "var(--keluar)" }}>
          <BellRing className="w-6 h-6 shrink-0" strokeWidth={2.4} />
          <div className="text-sm font-bold">{reminders.length} hutang mendekati / lewat jatuh tempo — segera bayar!</div>
        </div>
      )}

      <div className="flex gap-2">
        {[["all", "Semua"], ["unpaid", "Belum Lunas"], ["paid", "Lunas"]].map(([f, l]) => (
          <button key={f} data-testid={`debt-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 border-2 border-[#0A0A0A] rounded-lg text-sm font-bold ${filter === f ? "nb-shadow" : "bg-white"}`}
            style={filter === f ? { background: "var(--primary)" } : {}}>{l}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="nb-card p-10 text-center text-[#52525B]"><Wallet className="w-10 h-10 mx-auto mb-3" />Tidak ada data.</div>
      ) : (
        <div className="space-y-2">
          {shown.map((d) => (
            <div key={d.id} data-testid={`debt-row-${d.id}`} className="nb-card p-4 flex items-center gap-3">
              <button data-testid={`debt-pay-${d.id}`} onClick={() => togglePay(d.id)} className="shrink-0">
                {d.is_paid ? <CheckCircle2 className="w-7 h-7" style={{ color: "var(--masuk)" }} strokeWidth={2.4} /> : <Circle className="w-7 h-7 text-[#52525B]" strokeWidth={2.4} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate flex items-center gap-2">
                  {d.supplier}
                  {(() => { const di = dueInfo(d); return di ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border-2 border-[#0A0A0A]" style={{ background: di.color }}>{di.label}</span> : null; })()}
                </div>
                <span className="label-tag">
                  {d.date}{d.description ? ` • ${d.description}` : ""}
                  {d.due_date ? ` • tempo ${d.due_date}` : ""}
                  {d.is_paid && d.paid_date ? ` • Lunas ${d.paid_date}` : ""}
                </span>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-extrabold" style={d.is_paid ? { textDecoration: "line-through", color: "#52525B" } : {}}>{rupiah(d.amount)}</div>
                {user?.role === "admin" && (
                  <button data-testid={`debt-delete-${d.id}`} onClick={() => remove(d.id)} className="text-xs mt-1" style={{ color: "var(--retur)" }}><Trash2 className="w-4 h-4 inline" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Tambah Hutang" testid="debt-modal">
        <form onSubmit={save}>
          <Field label="Supplier"><input data-testid="debt-supplier" required value={form.supplier} onChange={set("supplier")} className="nb-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jumlah"><input data-testid="debt-amount" type="number" min="0" required value={form.amount} onChange={set("amount")} className="nb-input" /></Field>
            <Field label="Tanggal"><input data-testid="debt-date" type="date" required value={form.date} onChange={set("date")} className="nb-input" /></Field>
          </div>
          <Field label="Keterangan (opsional)"><input data-testid="debt-desc" value={form.description} onChange={set("description")} className="nb-input" /></Field>
          <Field label="Jatuh Tempo (opsional)"><input data-testid="debt-due" type="date" value={form.due_date || ""} onChange={set("due_date")} className="nb-input" /></Field>
          <button data-testid="save-debt-button" type="submit" className="nb-btn w-full py-3 mt-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>Simpan</button>
        </form>
      </Modal>
    </div>
  );
}
